import React, { useState, useEffect } from 'react';
import { Search, Edit, Trash2, Plus } from 'lucide-react';

function RecurringContracts() {
  const [recurringContracts, setRecurringContracts] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Client selection modal
  const [showClientModal, setShowClientModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearchTerm, setClientSearchTerm] = useState('');

  const [form, setForm] = useState({
    contract_name: '',
    company_id: '',
    amount: '',
    vat_amount: '',
    frequency: '매월',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    is_ongoing: true,
    status: '진행중',
    contract_type: '계좌이체',
    notes: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const [recurringResponse, companiesResponse] = await Promise.all([
        fetch(`${baseUrl}/api/recurring-contracts`),
        fetch(`${baseUrl}/api/companies`)
      ]);

      const recurringData = recurringResponse.ok ? await recurringResponse.json() : [];
      const companiesData = companiesResponse.ok ? await companiesResponse.json() : [];

      setRecurringContracts(Array.isArray(recurringData) ? recurringData : []);
      setCompanies(Array.isArray(companiesData) ? companiesData : []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumberForDisplay = (value) => {
    if (!value) return '';
    return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const openCreate = () => {
    setEditing(null);
    setSelectedClient(null);
    setForm({
      contract_name: '',
      company_id: '',
      amount: '',
      vat_amount: '',
      frequency: '매월',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      is_ongoing: true,
      status: '진행중',
      contract_type: '계좌이체',
      notes: ''
    });
    setShowModal(true);
  };

  const openEdit = (contract) => {
    setEditing(contract);
    setSelectedClient(contract.companies);
    setForm({
      contract_name: contract.contract_name || '',
      company_id: contract.company_id || '',
      amount: formatNumberForDisplay(contract.amount),
      vat_amount: formatNumberForDisplay(contract.vat_amount),
      frequency: contract.frequency || '매월',
      start_date: contract.start_date || '',
      end_date: contract.end_date || '',
      is_ongoing: contract.is_ongoing !== false,
      status: contract.status || '진행중',
      contract_type: contract.contract_type || '계좌이체',
      notes: contract.notes || ''
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditing(null);
    setSelectedClient(null);
  };

  const handleInputChange = (field, value) => {
    let newValue = value;

    if (field === 'amount' || field === 'vat_amount') {
      const cleanValue = value.replace(/[^\d,]/g, '').replace(/,/g, '');
      newValue = cleanValue ? cleanValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '';
    }

    setForm(prev => {
      const updated = { ...prev, [field]: newValue };

      // Auto-calculate VAT (10%) when amount changes
      if (field === 'amount') {
        const numericAmount = newValue.replace(/,/g, '');
        if (numericAmount && !isNaN(numericAmount)) {
          const vat = Math.round(parseFloat(numericAmount) * 0.1);
          updated.vat_amount = vat.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        } else {
          updated.vat_amount = '';
        }
      }

      // Clear end_date when switching to ongoing
      if (field === 'is_ongoing' && newValue) {
        updated.end_date = '';
      }

      return updated;
    });
  };

  const openClientModal = () => setShowClientModal(true);
  const closeClientModal = () => setShowClientModal(false);
  const selectClient = (client) => {
    setSelectedClient(client);
    setForm(prev => ({ ...prev, company_id: client.id }));
    setShowClientModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.company_id) {
      alert('클라이언트를 선택해주세요.');
      return;
    }

    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const url = editing
        ? `${baseUrl}/api/recurring-contracts/${editing.id}`
        : `${baseUrl}/api/recurring-contracts`;
      const method = editing ? 'PUT' : 'POST';

      const submitData = {
        ...form,
        amount: form.amount ? parseFloat(form.amount.replace(/,/g, '')) : null,
        vat_amount: form.vat_amount ? parseFloat(form.vat_amount.replace(/,/g, '')) : null
      };

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        closeModal();
        loadData();
      } else {
        const errorData = await response.json();
        alert(`오류: ${errorData.error}`);
      }
    } catch (error) {
      console.error('Error saving recurring contract:', error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('정말로 이 정기 계약을 삭제하시겠습니까?')) return;

    try {
      const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
      const response = await fetch(`${baseUrl}/api/recurring-contracts/${id}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        loadData();
      } else {
        alert('삭제 중 오류가 발생했습니다.');
      }
    } catch (error) {
      console.error('Error deleting recurring contract:', error);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  // Estimated monthly-normalized value of a recurring contract (for display)
  const monthlyEquivalent = (contract) => {
    const amount = parseFloat((contract.amount || 0).toString().replace(/,/g, '')) || 0;
    const perYear = { '매주': 52, '격주': 26, '매월': 12, '분기': 4, '반기': 2, '매년': 1 };
    return Math.round((amount * (perYear[contract.frequency] || 12)) / 12);
  };

  const filteredContracts = recurringContracts.filter(c => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (c.contract_name && c.contract_name.toLowerCase().includes(term)) ||
           (c.companies?.client_name && c.companies.client_name.toLowerCase().includes(term));
  });

  const filteredClients = companies.filter(client => {
    const term = clientSearchTerm.toLowerCase();
    return client.client_name.toLowerCase().includes(term) ||
           (client.company_name && client.company_name.toLowerCase().includes(term));
  });

  // Total monthly-normalized recurring income across active contracts
  const totalMonthly = recurringContracts
    .filter(c => c.status === '진행중')
    .reduce((sum, c) => sum + monthlyEquivalent(c), 0);

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="page-container project-management-table">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1>정기 계약</h1>
        <div style={{ color: '#6b7280', fontSize: '0.95em' }}>
          월 환산 합계: <strong style={{ color: '#2563eb' }}>{formatNumberForDisplay(totalMonthly)}원</strong>
        </div>
      </div>

      <div className="controls" style={{ display: 'flex', alignItems: 'center', gap: '0.5em', flexWrap: 'nowrap' }}>
        <input
          type="text"
          className="form-input"
          style={{ minWidth: 180, marginRight: 0 }}
          placeholder="계약명 또는 클라이언트명 검색..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
        />
        <button className="btn btn-primary" onClick={openCreate} style={{ whiteSpace: 'nowrap', marginLeft: 'auto' }}>
          <Plus size={16} />
          정기 계약 생성
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>계약명</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>클라이언트</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>금액</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>부가세</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>주기</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>월 환산</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>계약 기간</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>진행</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>계약 종류</th>
              <th style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>작업</th>
            </tr>
          </thead>
          <tbody>
            {filteredContracts.map((contract) => (
              <tr key={contract.id}>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>
                  <strong>{contract.contract_name}</strong>
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>
                  <span className={`medium-badge ${contract.companies?.medium || 'default'}`} style={{ fontSize: '0.8rem' }}>
                    {contract.companies?.client_name || '-'}
                  </span>
                </td>
                <td className="amount" style={{ textAlign: 'center', fontWeight: 'bold', fontSize: '0.9em' }}>
                  {contract.amount ? formatNumberForDisplay(contract.amount) : '-'}
                </td>
                <td className="amount" style={{ textAlign: 'center', fontSize: '0.9em' }}>
                  {contract.vat_amount ? formatNumberForDisplay(contract.vat_amount) : '-'}
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>
                  <span className="contract-badge">{contract.frequency}</span>
                </td>
                <td className="amount" style={{ textAlign: 'center', fontSize: '0.9em', color: '#2563eb' }}>
                  {contract.amount ? formatNumberForDisplay(monthlyEquivalent(contract)) : '-'}
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em', whiteSpace: 'pre-line' }}>
                  {contract.start_date || '-'}
                  {'\n'}
                  {contract.is_ongoing ? '진행 중 (무기한)' : (contract.end_date || '-')}
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>
                  <span className={`status-badge ${contract.status === '진행중' ? '진행중' : ''}`}>{contract.status}</span>
                </td>
                <td style={{ textAlign: 'center', fontSize: '0.9em', padding: '0.5em' }}>
                  <span className={`contract-badge contract-${contract.contract_type}`}>{contract.contract_type}</span>
                </td>
                <td style={{ padding: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.3em' }}>
                    <Edit
                      size={20}
                      style={{ cursor: 'pointer', color: '#6c757d' }}
                      onClick={() => openEdit(contract)}
                    />
                    <Trash2
                      size={20}
                      style={{ cursor: 'pointer', color: '#e74c3c' }}
                      onClick={() => handleDelete(contract.id)}
                    />
                  </div>
                </td>
              </tr>
            ))}
            {filteredContracts.length === 0 && (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                  등록된 정기 계약이 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <p>총 {filteredContracts.length}개 정기 계약</p>
      </div>

      {/* Create/Edit Recurring Contract Modal */}
      {showModal && (
        <div className="form-overlay">
          <div className="form-modal">
            <h2>{editing ? '정기 계약 수정' : '새 정기 계약 추가'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">계약명 *</label>
                <input
                  type="text"
                  className="form-input"
                  value={form.contract_name}
                  onChange={(e) => handleInputChange('contract_name', e.target.value)}
                  placeholder="예: 월 유지보수 계약"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">클라이언트 선택 *</label>
                <div className="client-selector">
                  <div className="search-box" onClick={openClientModal} style={{ cursor: 'pointer' }}>
                    <Search size={20} />
                    <input
                      type="text"
                      placeholder="클라이언트 검색..."
                      value={selectedClient ? `${selectedClient.client_name}${selectedClient.company_name ? ` - ${selectedClient.company_name}` : ''}` : ''}
                      className="form-input"
                      readOnly
                      style={{ cursor: 'pointer' }}
                    />
                  </div>
                  <button type="button" className="btn btn-secondary" onClick={openClientModal}>
                    선택
                  </button>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">금액</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    placeholder="500,000"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">부가세</label>
                  <input
                    type="text"
                    className="form-input"
                    value={form.vat_amount}
                    onChange={(e) => handleInputChange('vat_amount', e.target.value)}
                    placeholder="50,000"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">결제 주기</label>
                  <select
                    value={form.frequency}
                    onChange={(e) => handleInputChange('frequency', e.target.value)}
                    className="form-input"
                  >
                    <option value="매주">매주</option>
                    <option value="격주">격주 (2주마다)</option>
                    <option value="매월">매월</option>
                    <option value="분기">분기 (3개월마다)</option>
                    <option value="반기">반기 (6개월마다)</option>
                    <option value="매년">매년</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">계약 종류</label>
                  <select
                    value={form.contract_type}
                    onChange={(e) => handleInputChange('contract_type', e.target.value)}
                    className="form-input"
                  >
                    <option value="계좌이체">계좌이체</option>
                    <option value="프리랜서">프리랜서</option>
                    <option value="사업자">사업자</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">시작일 *</label>
                  <input
                    type="date"
                    className="form-input"
                    value={form.start_date}
                    onChange={(e) => handleInputChange('start_date', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">종료일</label>
                  <input
                    type="date"
                    className="form-input"
                    value={form.end_date}
                    onChange={(e) => handleInputChange('end_date', e.target.value)}
                    disabled={form.is_ongoing}
                    style={form.is_ongoing ? { background: '#f3f4f6', cursor: 'not-allowed' } : {}}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <input
                    type="checkbox"
                    checked={form.is_ongoing}
                    onChange={(e) => handleInputChange('is_ongoing', e.target.checked)}
                    style={{ margin: 0 }}
                  />
                  진행 중 (종료일 없음 / 무기한)
                </label>
              </div>

              <div className="form-group">
                <label className="form-label">진행 상태</label>
                <select
                  value={form.status}
                  onChange={(e) => handleInputChange('status', e.target.value)}
                  className="form-input"
                >
                  <option value="진행중">진행중</option>
                  <option value="종료">종료</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">메모</label>
                <input
                  type="text"
                  className="form-input"
                  value={form.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="선택 사항"
                />
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={closeModal}>
                  취소
                </button>
                <button type="submit" className="btn btn-primary">
                  {editing ? '수정' : '추가'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Client Selection Modal */}
      {showClientModal && (
        <div className="form-overlay client-selection-overlay">
          <div className="form-modal">
            <div className="modal-header">
              <h2>클라이언트 선택</h2>
              <button onClick={closeClientModal} className="btn-icon">×</button>
            </div>
            <div className="modal-body">
              <div className="search-box">
                <Search size={20} />
                <input
                  type="text"
                  placeholder="클라이언트명 또는 상호명으로 검색..."
                  value={clientSearchTerm}
                  onChange={(e) => setClientSearchTerm(e.target.value)}
                  className="form-input"
                  autoFocus
                />
              </div>
              <div className="client-list">
                {filteredClients.map((client) => (
                  <div
                    key={client.id}
                    className="client-item"
                    onClick={() => selectClient(client)}
                  >
                    <div className="client-info">
                      <div className="client-details">
                        <span className="client-name">{client.client_name}</span>
                        {client.company_name && (
                          <span className="company-name"> - {client.company_name}</span>
                        )}
                      </div>
                      <div className="client-meta">
                        <span className={`medium-badge ${client.medium || 'default'}`}>
                          {client.medium || '기타'}
                        </span>
                        <span className={`business-badge ${client.is_business ? '사업자' : '개인'}`}>
                          {client.is_business ? '사업자' : '개인'}
                        </span>
                        {client.phone && (
                          <span className="phone">📞 {client.phone}</span>
                        )}
                        {client.email && (
                          <span className="email">✉️ {client.email}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecurringContracts;
